
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DevelopmentRecipe } from '@/types/develop';
import { Timer, Edit, Trash, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';

interface RecipeCardProps {
  recipe: DevelopmentRecipe;
  onStart: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleFavorite: () => void;
  darkMode?: boolean;
}

export const RecipeCard: React.FC<RecipeCardProps> = ({
  recipe,
  onStart,
  onEdit,
  onDelete,
  onToggleFavorite,
  darkMode = false
}) => {
  // Calculate total development time
  const totalTime = recipe.steps.reduce((total, step) => total + step.time, 0);
  
  // Format total time in minutes and seconds
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };
  
  return (
    <Card className={cn(
      'w-full transition-all duration-300 hover:shadow-md',
      darkMode && 'bg-black text-red-500 border-red-900'
    )}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <CardTitle className={cn('text-lg font-medium line-clamp-1', darkMode && 'text-red-400')}>
            {recipe.name}
          </CardTitle>
          
          {!recipe.isUserCreated && (
            <div className={cn(
              "bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs font-medium",
              darkMode && "bg-red-900 text-red-300"
            )}>
              Default
            </div>
          )}
        </div>
        
        <div className={cn('text-sm text-muted-foreground space-y-1', darkMode && 'text-red-700')}>
          {recipe.filmStock && (
            <div className="flex justify-between">
              <span>Film:</span> <span>{recipe.filmStock} {recipe.iso && `(${recipe.iso} ISO)`}</span>
            </div>
          )}
          
          {recipe.developer && (
            <div className="flex justify-between">
              <span>Developer:</span> <span>{recipe.developer} ({recipe.temperature}°C)</span>
            </div>
          )}
          
          <div className="flex justify-between">
            <span>Development:</span> 
            <span>
              {recipe.pushPull !== 'standard' ? (
                <>
                  {recipe.pushPull === 'push' ? 'Push' : 'Pull'} 
                  {recipe.pushPullStops && recipe.pushPullStops > 0 
                    ? ` (${recipe.pushPullStops} ${recipe.pushPullStops === 1 ? 'stop' : 'stops'})`
                    : ''}
                </>
              ) : (
                'Normal'
              )}
            </span>
          </div>
          
          <div className="flex justify-between">
            <span>Total Time:</span> <span>{formatTime(totalTime)}</span>
          </div>
          
          <div className="flex justify-between">
            <span>Steps:</span> <span>{recipe.steps.length}</span>
          </div>
          
          <div className="flex justify-between">
            <span>Last Used:</span> 
            <span>{formatDistanceToNow(new Date(recipe.updatedAt), { addSuffix: true })}</span>
          </div>
        </div>
      </CardHeader>
      
      <CardFooter className="flex justify-between pt-4">
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant={darkMode ? "destructive" : "default"} 
            onClick={onStart}
            className={darkMode ? 'bg-red-900 hover:bg-red-800 text-red-100' : ''}
          >
            <Timer className="mr-1 h-4 w-4" />
            Start
          </Button>
          
          <Button 
            size="sm" 
            variant="outline" 
            onClick={onToggleFavorite}
            className={darkMode ? 'border-red-900 text-red-400 hover:bg-red-950' : ''}
          >
            {recipe.isFavorite ? (
              <Heart className="h-4 w-4 fill-rose-500 text-rose-500" />
            ) : (
              <Heart className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={onEdit}
            className={darkMode ? 'text-red-400 hover:bg-red-950 hover:text-red-300' : ''}
          >
            <Edit className="h-4 w-4" />
          </Button>
          
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={onDelete}
            className={darkMode ? 'text-red-400 hover:bg-red-950 hover:text-red-300' : ''}
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
